import { useEffect, useRef, useState } from "react";
import {
  Clapperboard, Crosshair, Edit3, Eye, EyeOff, Loader2, Plus, Search, Star, Trash2, Upload, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError, API_BASE } from "../../lib/api";

const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3 py-2 text-sm text-slate-200 outline-none focus:border-saffron";
const TYPES = ["video", "image", "reel", "article", "press_release", "event", "achievement"];
const CATEGORIES = ["How To Pack", "Influencers", "Customer Stories", "Customer Feedback", "PR & Media",
  "Events", "Team IndiaFast", "Achievements", "Behind the Scenes", "Announcements", "Photos"];
const PLATFORMS = ["", "Instagram", "YouTube", "Facebook", "LinkedIn", "Website", "Internal"];
const FOCAL_PRESETS = [["Top", 50, 15], ["Center", 50, 50], ["Bottom", 50, 85], ["Left", 15, 50], ["Right", 85, 50]];

const emptyForm = {
  media_type: "image", category: "Photos", title: "", description: "", file_path: "", card_path: "",
  thumb_path: "", mobile_path: "", external_url: "", date: "", location: "", creator: "", platform: "",
  duration: "", featured: false, published: true, is_hero: false, display_order: 0, tags: "",
  focal_x: 50, focal_y: 50, width: null, height: null,
};

const thumbOf = (m) => (m.thumb_path || m.card_path || m.file_path
  ? `${API_BASE}/api/mediahub/public/file/${m.id}/${m.thumb_path || m.card_path ? "thumb" : "orig"}` : null);

function FocalPicker({ form, setForm }) {
  const src = form.file_path && form.id ? `${API_BASE}/api/mediahub/public/file/${form.id}/card`
    : form.previewUrl || null;
  return (
    <div>
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">Focal Point (protects faces/subjects when cropped)</p>
      {src && (
        <div className="relative mb-2 inline-block cursor-crosshair overflow-hidden rounded-lg border border-adminborder"
             data-testid="focal-picker"
             onClick={(e) => {
               const r = e.currentTarget.getBoundingClientRect();
               setForm({ ...form, focal_x: Math.round(((e.clientX - r.left) / r.width) * 100), focal_y: Math.round(((e.clientY - r.top) / r.height) * 100) });
             }}>
          <img src={src} alt="focal preview" className="h-36 w-auto max-w-full object-cover" style={{ objectPosition: `${form.focal_x}% ${form.focal_y}%` }} />
          <span className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-saffron bg-saffron/30"
                style={{ left: `${form.focal_x}%`, top: `${form.focal_y}%` }} />
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {FOCAL_PRESETS.map(([label, x, y]) => (
          <button type="button" key={label} onClick={() => setForm({ ...form, focal_x: x, focal_y: y })} data-testid={`focal-${label.toLowerCase()}`}
                  className={`rounded-full px-3 py-1 text-[10px] font-bold ${form.focal_x === x && form.focal_y === y ? "bg-saffron text-white" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}>
            {label}
          </button>
        ))}
        <span className="ml-1 self-center text-[10px] text-slate-500"><Crosshair className="mr-1 inline h-3 w-3" />or click the image to set a custom point</span>
      </div>
    </div>
  );
}

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: "", category: "", status: "", q: "" });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [bulkProgress, setBulkProgress] = useState(null);
  const bulkRef = useRef(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    api.get(`/mediahub/admin/items?${params}`).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.type, filters.category, filters.status, filters.q]);

  const uploadImage = async (file, target) => {
    setUploading(target);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/mediahub/admin/upload-image", fd);
      if (target === "main") {
        setForm((f) => ({ ...f, file_path: r.data.path, card_path: r.data.card_path, thumb_path: r.data.thumb_path,
                           width: r.data.width, height: r.data.height, previewUrl: `${API_BASE}/api/files/${r.data.thumb_path}?auth=${localStorage.getItem("if_token")}` }));
      } else if (target === "mobile") {
        setForm((f) => ({ ...f, mobile_path: r.data.path }));
      } else {
        setForm((f) => ({ ...f, thumb_path: r.data.path, card_path: f.card_path || r.data.card_path }));
      }
      toast.success("Image optimized & uploaded (WebP card + thumbnail generated)");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(""); }
  };

  const uploadVideo = async (file) => {
    setUploading("video");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd);
      setForm((f) => ({ ...f, file_path: r.data.path }));
      toast.success("Video uploaded");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(""); }
  };

  const onMainFile = (file) => {
    if (!file) return;
    if (file.type.startsWith("video/")) {
      if (form.media_type === "image") { toast.error("Media type is Image — switch type to Video/Reel first"); return; }
      uploadVideo(file);
    } else uploadImage(file, "main");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, tags: String(form.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
                        display_order: parseInt(form.display_order) || 0 };
      delete payload.previewUrl;
      delete payload.id;
      delete payload.views;
      delete payload.created_at;
      if (form.id) await api.put(`/mediahub/admin/items/${form.id}`, payload);
      else await api.post("/mediahub/admin/items", payload);
      toast.success(form.id ? "Media updated" : "Media published");
      setForm(null); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const del = async (m) => {
    if (!window.confirm(`Delete "${m.title}" permanently?`)) return;
    try { await api.delete(`/mediahub/admin/items/${m.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const toggle = async (m, key) => {
    try {
      const { id, views, created_at, ...rest } = m;
      await api.put(`/mediahub/admin/items/${m.id}`, { ...rest, [key]: !m[key], tags: m.tags || [] });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const bulkUpload = async (files) => {
    if (!files?.length) return;
    setBulkProgress({ done: 0, total: files.length });
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const isVideo = f.type.startsWith("video/");
        const r = await api.post(isVideo ? "/uploads" : "/mediahub/admin/upload-image", fd);
        await api.post("/mediahub/admin/items", {
          media_type: isVideo ? "video" : "image", category: "Photos", title: f.name.replace(/\.[^.]+$/, ""),
          file_path: r.data.path, card_path: r.data.card_path || "", thumb_path: r.data.thumb_path || "",
          width: r.data.width || null, height: r.data.height || null, published: false, tags: [],
        });
      } catch (err) { toast.error(`${f.name}: ${apiError(err)}`); }
      setBulkProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    toast.success("Bulk upload complete — items saved as Drafts in Photos");
    setBulkProgress(null); load();
  };

  return (
    <div data-testid="admin-media-library">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">Media Library</h1>
          <p className="mt-1 text-sm text-slate-500">Everything published here appears automatically on the Media &amp; Videos page.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => bulkRef.current?.click()} data-testid="media-bulk-upload-btn"
                  className="flex items-center gap-2 rounded-lg border border-adminborder px-4 py-2 text-xs font-bold text-slate-300 hover:border-saffron hover:text-saffron">
            <Upload className="h-3.5 w-3.5" /> Bulk Upload
          </button>
          <input ref={bulkRef} type="file" multiple accept="image/*,video/mp4,video/quicktime" className="hidden" data-testid="media-bulk-input"
                 onChange={(e) => { bulkUpload([...e.target.files]); e.target.value = ""; }} />
          <button onClick={() => setForm({ ...emptyForm })} data-testid="media-add-btn"
                  className="flex items-center gap-2 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover">
            <Plus className="h-3.5 w-3.5" /> Add Media
          </button>
        </div>
      </div>

      {bulkProgress && (
        <div className="mt-4 rounded-xl border border-saffron/30 bg-saffron/10 p-4" data-testid="media-bulk-progress">
          <p className="text-xs font-bold text-saffron">Uploading {bulkProgress.done}/{bulkProgress.total}…</p>
          <div className="mt-2 h-1.5 rounded-full bg-white/10"><span className="block h-full rounded-full bg-saffron transition-all" style={{ width: `${(bulkProgress.done / bulkProgress.total) * 100}%` }} /></div>
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2" data-testid="media-filters">
        <div className="flex items-center gap-2 rounded-lg border border-adminborder bg-adminbg px-3">
          <Search className="h-3.5 w-3.5 text-slate-500" />
          <input className="bg-transparent py-2 text-sm text-slate-200 outline-none" placeholder="Search title…" data-testid="media-filter-search"
                 value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
        </div>
        <select className={`${darkInput} max-w-[150px]`} data-testid="media-filter-type" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
          <option value="">All Types</option>{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className={`${darkInput} max-w-[180px]`} data-testid="media-filter-category" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
          <option value="">All Categories</option>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={`${darkInput} max-w-[140px]`} data-testid="media-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All Status</option><option value="published">Published</option><option value="draft">Draft</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-20 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      ) : !items.length ? (
        <div className="mt-6 rounded-xl border border-adminborder bg-admincard p-14 text-center" data-testid="media-library-empty">
          <Clapperboard className="mx-auto h-10 w-10 text-slate-600" />
          <p className="mt-3 text-sm font-semibold text-slate-300">No media yet — upload your first photo or video.</p>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="media-library-grid">
          {items.map((m) => (
            <div key={m.id} className="overflow-hidden rounded-xl border border-adminborder bg-admincard" data-testid={`media-item-${m.id}`}>
              <div className="relative aspect-video overflow-hidden bg-adminbg">
                {thumbOf(m) ? (
                  <img src={thumbOf(m)} alt={m.title} loading="lazy" className="h-full w-full object-cover"
                       style={{ objectPosition: `${m.focal_x ?? 50}% ${m.focal_y ?? 50}%` }} />
                ) : (
                  <div className="grid h-full place-items-center"><Clapperboard className="h-8 w-8 text-slate-600" /></div>
                )}
                <div className="absolute left-2 top-2 flex gap-1">
                  <span className="rounded bg-navy/85 px-2 py-0.5 text-[9px] font-bold uppercase text-saffron">{m.media_type}</span>
                  {m.featured && <span className="rounded bg-saffron px-2 py-0.5 text-[9px] font-bold uppercase text-white">Featured</span>}
                  {m.is_hero && <span className="rounded bg-emerald-600 px-2 py-0.5 text-[9px] font-bold uppercase text-white">Hero</span>}
                </div>
                <span className={`absolute right-2 top-2 rounded px-2 py-0.5 text-[9px] font-bold uppercase ${m.published ? "bg-emerald-600/90 text-white" : "bg-slate-600/90 text-slate-200"}`}>
                  {m.published ? "Live" : "Draft"}
                </span>
              </div>
              <div className="p-3.5">
                <p className="line-clamp-1 text-sm font-bold text-white">{m.title}</p>
                <p className="mt-0.5 text-[11px] text-slate-500">{m.category}{m.date ? ` · ${m.date}` : ""} · order {m.display_order}</p>
                <div className="mt-3 flex items-center gap-1.5">
                  <button onClick={() => setForm({ ...m, tags: (m.tags || []).join(", "), previewUrl: thumbOf(m) })} data-testid={`media-edit-${m.id}`}
                          className="flex items-center gap-1 rounded-lg border border-adminborder px-2.5 py-1.5 text-[10px] font-bold text-slate-300 hover:border-saffron hover:text-saffron">
                    <Edit3 className="h-3 w-3" /> Edit
                  </button>
                  <button onClick={() => toggle(m, "published")} data-testid={`media-publish-${m.id}`}
                          className="flex items-center gap-1 rounded-lg border border-adminborder px-2.5 py-1.5 text-[10px] font-bold text-slate-300 hover:border-emerald-400 hover:text-emerald-400">
                    {m.published ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />} {m.published ? "Unpublish" : "Publish"}
                  </button>
                  <button onClick={() => toggle(m, "featured")} data-testid={`media-feature-${m.id}`}
                          className={`rounded-lg border px-2.5 py-1.5 text-[10px] font-bold ${m.featured ? "border-saffron text-saffron" : "border-adminborder text-slate-300 hover:border-saffron hover:text-saffron"}`}>
                    <Star className="h-3 w-3" />
                  </button>
                  <button onClick={() => del(m)} data-testid={`media-delete-${m.id}`}
                          className="ml-auto rounded-lg bg-red-500/10 p-1.5 text-red-400 hover:bg-red-500/20"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4" data-testid="media-form-modal">
          <div className="mx-auto my-8 w-full max-w-2xl rounded-xl border border-adminborder bg-admincard p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-white">{form.id ? "Edit Media" : "Add Media"}</h3>
              <button onClick={() => setForm(null)} className="grid h-8 w-8 place-items-center rounded-lg bg-white/5 text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <select className={darkInput} data-testid="media-form-type" value={form.media_type} onChange={(e) => setForm({ ...form, media_type: e.target.value })}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select className={darkInput} data-testid="media-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input className={`${darkInput} sm:col-span-2`} data-testid="media-form-title" placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <textarea rows={2} className={`${darkInput} sm:col-span-2`} data-testid="media-form-description" placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

              <div className="sm:col-span-2">
                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">Main Media (image auto-optimized · MP4/MOV video · or external URL below)</p>
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-adminborder bg-adminbg px-4 py-5 text-xs font-bold text-slate-400 hover:border-saffron hover:text-saffron" data-testid="media-form-upload">
                  {uploading === "main" || uploading === "video" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {form.file_path ? "Media uploaded — click to replace" : "Drop/choose image or video"}
                  <input type="file" accept="image/*,video/mp4,video/quicktime" className="hidden" onChange={(e) => onMainFile(e.target.files[0])} />
                </label>
                {form.width && <p className="mt-1 text-[10px] text-emerald-400">✓ {form.width}×{form.height} — WebP card &amp; thumbnail auto-generated</p>}
              </div>

              <input className={`${darkInput} sm:col-span-2`} data-testid="media-form-external" placeholder="External URL (YouTube / Vimeo / article link) — optional" value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} />

              <div className="sm:col-span-2"><FocalPicker form={form} setForm={setForm} /></div>

              <input type="date" className={darkInput} data-testid="media-form-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              <input className={darkInput} data-testid="media-form-location" placeholder="Location (optional)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              <input className={darkInput} data-testid="media-form-creator" placeholder="Creator / person name (optional)" value={form.creator} onChange={(e) => setForm({ ...form, creator: e.target.value })} />
              <select className={darkInput} data-testid="media-form-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
                {PLATFORMS.map((p) => <option key={p} value={p}>{p || "Platform (optional)"}</option>)}
              </select>
              <input className={darkInput} data-testid="media-form-duration" placeholder="Duration e.g. 02:31 (optional)" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
              <input type="number" className={darkInput} data-testid="media-form-order" placeholder="Display order (0 = first)" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} />
              <input className={`${darkInput} sm:col-span-2`} data-testid="media-form-tags" placeholder="Tags, comma separated" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />

              <div className="flex flex-wrap gap-4 sm:col-span-2">
                {[["featured", "Featured"], ["published", "Published"], ["is_hero", "Set as Hero"]].map(([k, label]) => (
                  <label key={k} className="flex cursor-pointer items-center gap-2 text-xs font-bold text-slate-300" data-testid={`media-form-${k}`}>
                    <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} className="h-4 w-4 accent-saffron" /> {label}
                  </label>
                ))}
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setForm(null)} className="rounded-lg border border-adminborder px-4 py-2 text-sm font-bold text-slate-300">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="media-form-save"
                      className="flex items-center gap-2 rounded-lg bg-saffron px-5 py-2 text-sm font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {form.id ? "Save Changes" : form.published ? "Publish" : "Save Draft"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
